import React from 'react'
import {
  Box,
  Display,
  withCitrusTheme,
  Image as CitrusImage,
  AlignItems,
  JustifyContent,
  Icon,
  Color,
  FlexDirection,
  IconVariant,
  Caption2,
  Heading6,
  Body2,
  FontWeight,
  Overflow,
  AlignSelf,
} from '@gousto-internal/citrus-react'
import { Recipe, RecipeUUID } from '@library/menu-service'
import {
  getDefaultImage,
  getMoodImage,
  getSrcSet,
  getSurchargeLabel,
} from '../../../utils/formatters'
import { activateOnKeyboard } from './keyboard'
import { QuantityControl } from './QuantityControl'

const ContainedImage = withCitrusTheme(CitrusImage, () => ({
  display: 'block',
  objectFit: 'cover',
  objectPosition: 'center',
  borderRadius: '0',
}))

const RTHBadge = withCitrusTheme(Caption2, (theme) => ({
  color: theme.color(Color.White),
  backgroundColor: theme.color(Color.Warning_900),
  borderRadius: '3px',
  width: 'fit-content',
  padding: `${theme.spacing(0.5)}px ${theme.spacing(2)}px`,
  marginBottom: `${theme.spacing(1)}px`,
}))

type RecipeProps = {
  recipe: Recipe
  quantity: number
  numPortions: number
  addRecipe: (id: RecipeUUID, view: string) => void
  removeRecipe: (recipe: Recipe) => void
  onClickRecipe: (id: RecipeUUID) => void
  dataNoCode?: {
    name?: string
    servings?: string
    add?: string
  }
}

export const RecipeItem = ({
  recipe,
  quantity,
  numPortions,
  addRecipe,
  removeRecipe,
  onClickRecipe,
  dataNoCode = {},
}: RecipeProps) => {
  const image = getMoodImage(recipe.images)
  const surchargeLabel = getSurchargeLabel(recipe)
  const handleRecipeClick = () => onClickRecipe(recipe.id)
  const handleRecipeKeyDown = (event: React.KeyboardEvent) => {
    activateOnKeyboard(event, handleRecipeClick)
  }

  return (
    <Box
      key={recipe.id}
      display={Display.Flex}
      justifyContent={JustifyContent.FlexStart}
      alignItems={AlignItems.Center}
      gap={3}
      bg={Color.White}
      borderRadius={1.5}
      boxShadow="0 1px 6px rgba(0, 0, 0, 0.12)"
      paddingRight={2}
      color={Color.ColdGrey_900}
      data-testid="recipe"
      minHeight="92px"
      style={{ cursor: 'pointer', borderRadius: '6px' }}
      onClick={handleRecipeClick}
      onKeyDown={handleRecipeKeyDown}
      role="button"
      tabIndex={0}
    >
      <Box
        width="92px"
        minWidth="92px"
        minHeight="92px"
        alignSelf={AlignSelf.Stretch}
        overflowX={Overflow.Hidden}
        overflowY={Overflow.Hidden}
        style={{ borderRadius: '6px 0 0 6px' }}
      >
        <ContainedImage
          alt={recipe.name}
          loading="lazy"
          src={getDefaultImage(image)}
          srcSet={getSrcSet(image)}
          height="100%"
          width="92px"
        />
      </Box>
      <Box
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        justifyContent={JustifyContent.FlexStart}
        flexGrow={1}
        flexShrink={1}
        height="100%"
        minWidth="0"
        paddingV={2}
      >
        {recipe.preparationType === 'heat-only' && <RTHBadge>HEAT &amp; EAT</RTHBadge>}
        <Heading6
          data-no-code={dataNoCode.name}
          style={
            recipe.preparationType === 'heat-only'
              ? {
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }
              : {
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 3,
                  overflow: 'hidden',
                }
          }
        >
          {recipe.name}
        </Heading6>
        <Box display={Display.Flex} gap={2} style={{ minWidth: 0, flexWrap: 'wrap', rowGap: 0 }}>
          <Box color={Color.ColdGrey_600}>
            {recipe.preparationType === 'heat-only' ? (
              <Box display={Display.Flex} alignItems={AlignItems.Center} gap={1}>
                <Icon name="microwave" variant={IconVariant.Inherit} size={[4, 6]} />
                <Body2 color={Color.Inherit} style={{ whiteSpace: 'nowrap' }}>
                  {recipe.prepTime} mins (x{numPortions})
                </Body2>
              </Box>
            ) : (
              <Body2 color={Color.Inherit} style={{ whiteSpace: 'nowrap' }}>
                {recipe.prepTime} mins
              </Body2>
            )}
          </Box>
          <Body2 color={Color.ColdGrey_200}>|</Body2>
          <Body2 color={Color.ColdGrey_600} style={{ whiteSpace: 'nowrap' }}>
            {recipe.nutritionalInformation.perPortion.energyKcal} kcal
          </Body2>
        </Box>
        {surchargeLabel && (
          <Box display={Display.Flex} paddingV={1}>
            <Box bg={Color.NeutralGrey_50} style={{ borderRadius: '3px', padding: '2px 8px' }}>
              <Body2
                color={Color.ColdGrey_800}
                fontWeight={FontWeight.Bold}
                data-testid="recipe-surcharge"
              >
                {surchargeLabel}
              </Body2>
            </Box>
          </Box>
        )}
      </Box>
      <Box
        style={{
          flexShrink: 0,
          width: '84px',
        }}
      >
        <QuantityControl
          count={quantity}
          decrementIcon={quantity > 1 ? 'remove' : 'delete_outline'}
          decrementLabel={quantity > 1 ? 'Remove recipe' : 'Delete recipe'}
          onRemove={() => removeRecipe(recipe)}
          onAdd={() => addRecipe(recipe.id, 'modal_basket')}
          addLabel="Add recipe"
          countDataNoCode={dataNoCode.servings}
          addDataNoCode={dataNoCode.add}
        />
      </Box>
    </Box>
  )
}
